// js/shared/processor-inventory.js — every third party this product calls at
// runtime, what it receives, and why. The privacy policy's processor table is
// RENDERED from this file; it is not a paragraph somebody remembered to update.
//
// QUEUE ITEM BT. privacy.html was dated 13 May 2026 and named six processors.
// Between May and September the product acquired the care team, offline
// caching, voice notes, a food diary in the database, Open Food Facts barcode
// lookups and a Netlify form proxy — and the policy said none of it. One of
// those, Web3Forms, RECEIVES THE CONTACT FORM including the message body, and
// had never appeared in any disclosure at all. A policy that promises thirty
// days' notice of material change, while changing silently, is not merely out
// of date: it is the specific promise it breaks.
//
// THE FIX IS STRUCTURAL, NOT EDITORIAL. A list maintained by hand goes stale on
// the schedule of whoever is paying attention. So:
//
//   1. Every processor is a row here, with the HOSTS its code actually calls.
//   2. privacy.html renders §5 from this module (the same way the plain-language
//      summary is rendered from js/shared/privacy-summary.js — one set of
//      sentences, two surfaces, no copy to drift).
//   3. scripts/meta/META-52-processor-inventory.mjs scans the source for
//      outbound hosts in CALL positions and fails the build when it finds one
//      this file does not name — and when this file names a host no code calls.
//      Both directions, because a processor listed after it was removed is a
//      false disclosure in the other direction.
//
// `holdsClientData` IS THE LOAD-BEARING FLAG. Two questions get asked of this
// list and they are different questions. "Whose key do we rotate at 2am" is
// answered by the rows where a real person's information reaches the vendor —
// that is the set META-50 requires the breach runbook to cover. "What connects
// when this page loads" includes the font CDN and the video embed, which hold
// nothing about anybody and would be noise in a breach runbook and dishonest to
// omit from a privacy policy. Both sets live here; each gate reads the one it
// means.
//
// STATUS IS THE TRUTH ABOUT TODAY, not the roadmap. `active` means it runs for
// clients now. `dormant` means the code ships behind a flag that is off — the
// wearable aggregator is the standing example, and its rows say "Not active" on
// the page for exactly as long as that is true.
//
// PURE. No DOM at module scope, no fetch, no cross-realm state — the contract
// every pack in js/shared/ keeps, which is what lets node read it in a gate.

/**
 * One row per processor. `hosts` are the hostnames the code actually contacts;
 * META-52 matches on these, so a host added to the code without being added
 * here fails the build.
 *
 * en/es are the policy's own words. The Spanish is a faithful translation in
 * the `usted` register (es-MX neutralized, CLAUDE.md's variant rule) — the same
 * standard js/shared/legal-i18n.js holds itself to.
 */
export const PROCESSORS = Object.freeze([
  {
    id: 'supabase',
    name: 'Supabase',
    hosts: ['rvrvfdjmhwymkbmrzoza.supabase.co', 'supabase.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Supabase (Postgres database, authentication and file storage, hosted in the U.S.)',
      receives: 'Everything in your Client Portal record: your account, your health screening, your signed agreements, sessions, session notes, messages, invoices, habits, your food diary, your progress photos and your voice notes.',
      why: 'Service provider — they store and serve the application’s data. They do not use it for their own purposes.',
    },
    es: {
      role: 'Supabase (base de datos Postgres, autenticación y almacenamiento de archivos, alojado en EE. UU.)',
      receives: 'Todo lo que contiene su expediente en el Portal del Cliente: su cuenta, su cuestionario de salud, sus acuerdos firmados, sesiones, notas de sesión, mensajes, facturas, hábitos, su diario de alimentos, sus fotos de progreso y sus notas de voz.',
      why: 'Proveedor de servicio: almacenan y sirven los datos de la aplicación. No los usan para fines propios.',
    },
  },
  {
    id: 'netlify',
    name: 'Netlify',
    hosts: ['edge.netlify.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Netlify (web hosting and serverless functions, hosted in the U.S.)',
      receives: 'Web traffic to KinetiqAF.com and the Client Portal, and anything a form sends before it reaches its destination — including the public contact form and the public intake.',
      why: 'Hosting provider, and the server that holds our vendor keys so your browser never has to.',
    },
    es: {
      role: 'Netlify (alojamiento web y funciones sin servidor, alojado en EE. UU.)',
      receives: 'El tráfico web hacia KinetiqAF.com y el Portal del Cliente, y todo lo que envía un formulario antes de llegar a su destino, incluidos el formulario público de contacto y el formulario público de admisión.',
      why: 'Proveedor de alojamiento, y el servidor que guarda nuestras llaves de proveedor para que su navegador nunca tenga que hacerlo.',
    },
  },
  {
    id: 'stripe',
    name: 'Stripe',
    // KO — the owner's account facts (stripe-account, read-only) and the
    // dashboard link on Settings › Integrations › Stripe.
    hosts: ['js.stripe.com', 'api.stripe.com', 'dashboard.stripe.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Stripe (payments processor)',
      receives: 'Your name, your email address and the amount of an invoice when you pay. Your card number is collected by Stripe directly in their own form — we never see it, and we never store it. If you save a card, Stripe holds it and gives us a token that is useless anywhere else.',
      why: 'Payment processing and saved-card storage.',
    },
    es: {
      role: 'Stripe (procesador de pagos)',
      receives: 'Su nombre, su correo electrónico y el importe de una factura cuando usted paga. El número de su tarjeta lo recibe Stripe directamente en su propio formulario: nosotros nunca lo vemos ni lo guardamos. Si usted guarda una tarjeta, Stripe la resguarda y a nosotros nos entrega un token que no sirve en ningún otro lugar.',
      why: 'Procesamiento de pagos y resguardo de tarjetas guardadas.',
    },
  },
  {
    id: 'resend',
    name: 'Resend',
    hosts: ['api.resend.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Resend (transactional email delivery)',
      receives: 'Your email address and the contents of the emails we send you — session reminders and confirmations, invoices and receipts, account notices, and the acknowledgement you get after using the contact form. We do not send marketing email.',
      why: 'To deliver the email the app sends you.',
    },
    es: {
      role: 'Resend (envío de correo transaccional)',
      receives: 'Su correo electrónico y el contenido de los mensajes que le enviamos: recordatorios y confirmaciones de sesión, facturas y recibos, avisos de la cuenta y el acuse de recibo que recibe después de usar el formulario de contacto. No enviamos correo publicitario.',
      why: 'Para entregarle el correo que la aplicación le envía.',
    },
  },
  {
    id: 'web3forms',
    name: 'Web3Forms',
    hosts: ['api.web3forms.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Web3Forms (public contact-form delivery)',
      receives: 'What you type into the contact form on KinetiqAF.com: your name, your email address, your phone number, the service you selected and your message. They relay it to the trainer’s inbox.',
      why: 'To deliver the public contact form. This row was added in the September 2026 revision — the service has relayed the form since before this policy was first written and was not disclosed until now.',
    },
    es: {
      role: 'Web3Forms (entrega del formulario público de contacto)',
      receives: 'Lo que usted escribe en el formulario de contacto de KinetiqAF.com: su nombre, su correo electrónico, su teléfono, el servicio que seleccionó y su mensaje. Ellos lo retransmiten al buzón del entrenador.',
      why: 'Para entregar el formulario público de contacto. Esta fila se agregó en la revisión de septiembre de 2026: el servicio ha retransmitido el formulario desde antes de que se redactara esta política y no se había divulgado hasta ahora.',
    },
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    hosts: ['api.anthropic.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Anthropic (document reading and the optional AI assistant)',
      receives: 'When we file a paper document you give us — a health screening, a service agreement, a doctor’s note — we send the photograph to Anthropic’s API so the text can be read out of it instead of retyped. Where the optional AI assistant is switched on for your account, the program context it needs goes the same way. We use zero-data-retention mode, so nothing is kept on their side after the call returns.',
      why: 'To read paper into your record, and to run the AI features you can switch off in Settings › Privacy & data.',
    },
    es: {
      role: 'Anthropic (lectura de documentos y el asistente de IA opcional)',
      receives: 'Cuando archivamos un documento en papel que usted nos entrega —un cuestionario de salud, un acuerdo de servicio, una nota médica— enviamos la fotografía a la API de Anthropic para leer el texto en lugar de volver a capturarlo. Si el asistente de IA opcional está activado en su cuenta, el contexto del programa que necesita viaja por la misma vía. Usamos el modo de cero retención de datos, de modo que nada queda de su lado al terminar la llamada.',
      why: 'Para pasar el papel a su expediente y para ejecutar las funciones de IA que usted puede desactivar en Configuración › Privacidad y datos.',
    },
  },
  {
    id: 'onesignal',
    name: 'OneSignal',
    hosts: ['cdn.onesignal.com', 'api.onesignal.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'OneSignal (push notifications)',
      receives: 'Your push-notification token, and the text of the notification itself, if you turn on push alerts. The token identifies a browser, not a person.',
      why: 'To send you schedule, message and reminder alerts.',
    },
    es: {
      role: 'OneSignal (notificaciones push)',
      receives: 'Su token de notificación push y el texto de la notificación, si usted activa las alertas push. El token identifica un navegador, no a una persona.',
      why: 'Para enviarle avisos de horario, mensajes y recordatorios.',
    },
  },
  {
    id: 'google-maps',
    name: 'Google Maps Platform',
    hosts: ['maps.googleapis.com', 'maps.google.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Google (Maps Platform — address lookup and travel distance)',
      receives: 'The address text typed into an address field as it is being typed, so it can be completed and checked, and the start and end addresses of a trip when mileage is calculated. A session’s address is also rendered as an “open in Google Maps” link; following it hands that address to Google, the same as typing it there yourself.',
      why: 'So an in-home address is entered once and correctly, and so travel between sessions is measured rather than estimated.',
    },
    es: {
      role: 'Google (Maps Platform: búsqueda de direcciones y distancia de viaje)',
      receives: 'El texto de la dirección que se escribe en un campo de dirección, conforme se escribe, para poder completarla y verificarla, y las direcciones de inicio y fin de un trayecto cuando se calcula el kilometraje. La dirección de una sesión también se muestra como un enlace “abrir en Google Maps”; al seguirlo, esa dirección llega a Google, igual que si usted la escribiera ahí.',
      why: 'Para que una dirección a domicilio se capture una sola vez y de forma correcta, y para medir —no estimar— el traslado entre sesiones.',
    },
  },
  {
    id: 'ipify',
    name: 'ipify',
    hosts: ['api.ipify.org'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'ipify (IP address lookup)',
      receives: 'Nothing we send them. Your browser asks them what your public IP address is, which means they see the request — and therefore the address — at the moment you sign a document.',
      why: 'The Uniform Electronic Transactions Act asks a signature record to carry the circumstances of signing. The IP address is stored with the signature in our database, and only there.',
    },
    es: {
      role: 'ipify (consulta de dirección IP)',
      receives: 'Nada que nosotros les enviemos. Su navegador les pregunta cuál es su dirección IP pública, lo que significa que ellos ven la solicitud —y por lo tanto la dirección— en el momento en que usted firma un documento.',
      why: 'La Uniform Electronic Transactions Act exige que el registro de una firma conserve las circunstancias de la firma. La dirección IP se guarda junto con la firma en nuestra base de datos, y solo ahí.',
    },
  },
  {
    id: 'openfoodfacts',
    name: 'Open Food Facts',
    hosts: ['world.openfoodfacts.org'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Open Food Facts (barcode lookup, a non-profit food database)',
      receives: 'The barcode you scan. Nothing that identifies you travels with it — not your name, not your account, not your diary.',
      why: 'To turn a barcode into a food and its nutrition facts so you do not have to type them.',
    },
    es: {
      role: 'Open Food Facts (búsqueda por código de barras, base de datos de alimentos sin fines de lucro)',
      receives: 'El código de barras que usted escanea. Nada que lo identifique viaja con él: ni su nombre, ni su cuenta, ni su diario.',
      why: 'Para convertir un código de barras en un alimento y su información nutricional sin que usted tenga que escribirla.',
    },
  },
  {
    id: 'usda',
    name: 'USDA FoodData Central',
    hosts: ['api.nal.usda.gov'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'USDA FoodData Central (the U.S. government’s food composition database)',
      receives: 'The food you search for. The request is made by our server, not your browser, so they do not see your address or your account.',
      why: 'To look up the nutrition facts of a food you are logging.',
    },
    es: {
      role: 'USDA FoodData Central (base de datos de composición de alimentos del gobierno de EE. UU.)',
      receives: 'El alimento que usted busca. La solicitud la hace nuestro servidor, no su navegador, de modo que ellos no ven su dirección ni su cuenta.',
      why: 'Para consultar la información nutricional de un alimento que usted está registrando.',
    },
  },
  {
    id: 'venmo',
    name: 'Venmo',
    hosts: ['venmo.com'],
    holdsClientData: true,
    status: 'active',
    en: {
      role: 'Venmo (an optional way to pay an invoice)',
      receives: 'Nothing, unless you use it. An invoice email carries a “Pay with Venmo” button; the link we build carries the invoice number and the amount, so following it opens Venmo already filled in. Everything after that happens between you and Venmo under their terms, and we learn only that the money arrived.',
      why: 'So paying does not require a card if you would rather not use one.',
    },
    es: {
      role: 'Venmo (una forma opcional de pagar una factura)',
      receives: 'Nada, salvo que usted lo use. El correo de una factura incluye un botón “Pagar con Venmo”; el enlace que armamos lleva el número de factura y el importe, de modo que al seguirlo Venmo se abre ya con los datos. Todo lo que sigue ocurre entre usted y Venmo bajo los términos de ellos, y nosotros solo sabemos que el dinero llegó.',
      why: 'Para que pagar no requiera una tarjeta si usted prefiere no usarla.',
    },
  },
  {
    id: 'google-fonts',
    name: 'Google Fonts',
    hosts: ['fonts.googleapis.com', 'fonts.gstatic.com'],
    holdsClientData: false,
    status: 'active',
    en: {
      role: 'Google Fonts (typeface delivery)',
      receives: 'Nothing about you that we send. Your browser requests the font files, which means Google sees the request: your IP address and your browser type, the same as any website you load a file from.',
      why: 'The pages use the Inter and IBM Plex Mono typefaces.',
    },
    es: {
      role: 'Google Fonts (entrega de tipografías)',
      receives: 'Nada sobre usted que nosotros enviemos. Su navegador solicita los archivos de la tipografía, lo que significa que Google ve la solicitud: su dirección IP y el tipo de navegador, igual que en cualquier sitio del que se carga un archivo.',
      why: 'Las páginas usan las tipografías Inter e IBM Plex Mono.',
    },
  },
  {
    id: 'jsdelivr',
    name: 'jsDelivr',
    hosts: ['cdn.jsdelivr.net'],
    holdsClientData: false,
    status: 'active',
    en: {
      role: 'jsDelivr (open-source code delivery network)',
      receives: 'Nothing about you that we send. Your browser fetches open-source libraries from them when a feature needs one — the one that builds the export file when you download your data, and on the trainer’s side one that reads iPhone photos and one that fills in a PDF — so they see the request.',
      why: 'To deliver the libraries the app uses in the browser.',
    },
    es: {
      role: 'jsDelivr (red de entrega de código de fuente abierta)',
      receives: 'Nada sobre usted que nosotros enviemos. Su navegador obtiene de ellos bibliotecas de código abierto cuando una función las necesita —la que arma el archivo de exportación cuando usted descarga sus datos y, del lado del entrenador, una que lee fotos de iPhone y otra que llena un PDF—, de modo que ven la solicitud.',
      why: 'Para entregar las bibliotecas que la aplicación usa en el navegador.',
    },
  },
  {
    id: 'youtube',
    name: 'YouTube',
    hosts: ['www.youtube-nocookie.com', 'www.youtube.com'],
    holdsClientData: false,
    status: 'active',
    en: {
      role: 'YouTube (exercise demonstration videos)',
      receives: 'Nothing about you that we send. When an exercise has a demonstration video, it plays in an embedded player; we use the privacy-enhanced (no-cookie) embed, so YouTube is contacted when you press play and not before.',
      why: 'To show you how a movement is performed.',
    },
    es: {
      role: 'YouTube (videos de demostración de ejercicios)',
      receives: 'Nada sobre usted que nosotros enviemos. Cuando un ejercicio tiene video de demostración, este se reproduce en un reproductor incrustado; usamos la versión con privacidad mejorada (sin cookies), de modo que YouTube recibe la solicitud cuando usted presiona reproducir y no antes.',
      why: 'Para mostrarle cómo se ejecuta un movimiento.',
    },
  },
  {
    id: 'deno-registries',
    name: 'Deno and esm.sh module registries',
    hosts: ['deno.land', 'esm.sh'],
    holdsClientData: false,
    status: 'active',
    en: {
      role: 'deno.land and esm.sh (server-side code registries)',
      receives: 'Nothing about anybody. Our server functions load their own program code from these registries when they start. No request of yours reaches them and no data of yours is in one.',
      why: 'Listed because they are contacted at runtime and this table is generated from what the code calls, not from what would look tidy.',
    },
    es: {
      role: 'deno.land y esm.sh (registros de código del lado del servidor)',
      receives: 'Nada sobre ninguna persona. Nuestras funciones de servidor cargan su propio código de programa desde estos registros al iniciarse. Ninguna solicitud suya llega a ellos y ningún dato suyo se encuentra ahí.',
      why: 'Se enumeran porque se contactan en tiempo de ejecución y esta tabla se genera a partir de lo que el código llama, no de lo que se vería ordenado.',
    },
  },
  {
    id: 'terra',
    name: 'Terra',
    hosts: ['cdn.tryterra.co'],
    holdsClientData: true,
    status: 'dormant',
    en: {
      role: 'Terra (wearable-data aggregator) — not active',
      receives: 'Nothing today. If the practice switches this on, and you connect a device through it, Terra would receive the authorization you grant and the daily metrics you allow. We will update this row before that happens, not after.',
      why: 'To connect wearable devices through one integration instead of five. See §5a for the device makers themselves.',
    },
    es: {
      role: 'Terra (agregador de datos de dispositivos vestibles): no activo',
      receives: 'Nada hoy. Si la práctica lo activa y usted conecta un dispositivo a través de él, Terra recibiría la autorización que usted otorgue y las métricas diarias que permita. Actualizaremos esta fila antes de que eso ocurra, no después.',
      why: 'Para conectar dispositivos vestibles mediante una sola integración en lugar de cinco. Véase la §5a para los fabricantes de los dispositivos.',
    },
  },
  {
    id: 'wearable-makers',
    name: 'Garmin, Fitbit, Oura and WHOOP',
    hosts: ['connect.garmin.com', 'diauth.garmin.com', 'api.fitbit.com', 'api.ouraring.com', 'cloud.ouraring.com', 'api.prod.whoop.com', 'www.fitbit.com'],
    holdsClientData: true,
    status: 'dormant',
    en: {
      role: 'Garmin, Fitbit, Oura and WHOOP (device makers) — not active',
      receives: 'Nothing unless you connect that device. If you do, the maker receives the authorization you grant us and returns the metrics you allowed — steps, sleep, resting heart rate, workouts. Each provider’s own status is in §5a, and it says “Not active” until the integration is live.',
      why: 'To read the training and recovery numbers your device already records, so your trainer can see how your body is responding.',
    },
    es: {
      role: 'Garmin, Fitbit, Oura y WHOOP (fabricantes de dispositivos): no activo',
      receives: 'Nada, salvo que usted conecte ese dispositivo. Si lo hace, el fabricante recibe la autorización que usted nos otorga y devuelve las métricas que permitió: pasos, sueño, frecuencia cardiaca en reposo, entrenamientos. El estado de cada proveedor está en la §5a y dice “No activo” hasta que la integración esté en funcionamiento.',
      why: 'Para leer las cifras de entrenamiento y recuperación que su dispositivo ya registra, de modo que su entrenador pueda ver cómo responde su cuerpo.',
    },
  },
]);

/** Every hostname the inventory accounts for. META-52 matches against this. */
export const PROCESSOR_HOSTS = Object.freeze(
  PROCESSORS.reduce((acc, p) => acc.concat(p.hosts), []).sort()
);

/** The processors that receive information about a real person — the set the
 *  breach runbook has to cover (META-50 reads this, not the HTML). */
export function dataHoldingProcessors() {
  return PROCESSORS.filter((p) => p.holdsClientData);
}

/**
 * The policy table's rows, in one language. `status` is a word on the page, not
 * a CSS class: a reader deserves to be told "not active" in a sentence.
 */
export function processorRows(lang) {
  const es = String(lang || '').slice(0, 2).toLowerCase() === 'es';
  return PROCESSORS.map((p) => {
    const copy = es ? p.es : p.en;
    return {
      id: p.id,
      role: copy.role,
      receives: copy.receives,
      why: copy.why,
      status: p.status,
      statusLabel: p.status === 'dormant'
        ? (es ? 'No activo' : 'Not active')
        : (es ? 'Activo' : 'Active'),
    };
  });
}

/** The table's column headings, so the page carries no literal of its own. */
export function processorTableHeadings(lang) {
  const es = String(lang || '').slice(0, 2).toLowerCase() === 'es';
  return es
    ? { role: 'Destinatario', receives: 'Qué recibe', why: 'Para qué', status: 'Estado' }
    : { role: 'Recipient', receives: 'What they receive', why: 'Why', status: 'Status' };
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    PROCESSORS, PROCESSOR_HOSTS, processorRows, processorTableHeadings, dataHoldingProcessors,
  });
}
